import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Scatter,
  ScatterChart,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  ShoppingCart,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Eye,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  Target,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
} from "lucide-react";
import * as XLSX from "xlsx";
import apiService from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { toast } from "react-hot-toast";
import useRealTimeData from "../hooks/useRealTimeData";

export default function Reports() {
  const [selectedDashboard, setSelectedDashboard] = useState("overview");
  const [dateRange, setDateRange] = useState("month");
  const [filters, setFilters] = useState({
    customer: "all",
    product: "all",
    status: "all",
  });

  // Real-time data hooks for different dashboard types
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
    lastRefresh: dashboardLastRefresh,
    refresh: refreshDashboard,
  } = useRealTimeData(
    () => fetchDashboardData(),
    30000, // 30 seconds
    true, // Enable notifications
    [selectedDashboard, dateRange, filters]
  );

  const fetchDashboardData = async () => {
    try {
      const dateParams = getDateFilterParams();

      // Fetch data based on selected dashboard
      const promises = [];

      if (selectedDashboard === "overview" || selectedDashboard === "sales") {
        promises.push(
          apiService
            .getDashboardReport()
            .then((result) => ({ type: "dashboard", data: result })),
          apiService
            .getSalesAnalytics(dateParams)
            .then((result) => ({ type: "sales", data: result })),
          apiService
            .getOrderTrends(dateParams)
            .then((result) => ({ type: "orders", data: result }))
        );
      }

      if (selectedDashboard === "collections") {
        promises.push(
          apiService
            .getCollectionsReport(dateParams)
            .then((result) => ({ type: "collections", data: result })),
          apiService
            .getPaymentAnalytics(dateParams)
            .then((result) => ({ type: "invoices", data: result }))
        );
      }

      if (selectedDashboard === "customers") {
        promises.push(
          apiService
            .getCustomerAnalytics(dateParams)
            .then((result) => ({ type: "customers", data: result }))
        );
      }

      const results = await Promise.all(promises);

      const newData = {};
      results.forEach((result) => {
        newData[result.type] = result.data;
      });

      return newData;
    } catch (error) {
      console.error("Failed to fetch report data:", error);
      throw new Error("Failed to load report data");
    }
  };

  const getDateFilterParams = () => {
    const now = new Date();
    let startDate,
      endDate = now.toISOString();

    switch (dateRange) {
      case "week":
        startDate = new Date(
          now.getTime() - 7 * 24 * 60 * 60 * 1000
        ).toISOString();
        break;
      case "month":
        startDate = new Date(
          now.getTime() - 30 * 24 * 60 * 60 * 1000
        ).toISOString();
        break;
      case "quarter":
        startDate = new Date(
          now.getTime() - 90 * 24 * 60 * 60 * 1000
        ).toISOString();
        break;
      case "year":
        startDate = new Date(
          now.getTime() - 365 * 24 * 60 * 60 * 1000
        ).toISOString();
        break;
      default:
        startDate = new Date(
          now.getTime() - 30 * 24 * 60 * 60 * 1000
        ).toISOString();
    }

    return { startDate, endDate };
  };

  // Export report to Excel
  const handleExportReport = () => {
    try {
      if (!dashboardData) {
        toast.error("No data available to export");
        return;
      }

      const wb = XLSX.utils.book_new();
      const dateRangeLabel =
        dateRange.charAt(0).toUpperCase() + dateRange.slice(1);
      const dashboardLabel =
        dashboards.find((d) => d.id === selectedDashboard)?.label || "Report";

      // Export based on selected dashboard
      if (selectedDashboard === "overview" || selectedDashboard === "sales") {
        // Sales Data
        if (dashboardData.sales?.salesByDate) {
          const salesData = dashboardData.sales.salesByDate.map((item) => ({
            Date: new Date(item.date).toLocaleDateString(),
            Revenue: item.revenue,
            Orders: item.orders,
            "Average Order Value": item.orders
              ? (item.revenue / item.orders).toFixed(2)
              : 0,
          }));
          const salesWs = XLSX.utils.json_to_sheet(salesData);
          XLSX.utils.book_append_sheet(wb, salesWs, "Sales by Date");
        }

        // Product Performance
        if (dashboardData.sales?.salesByProduct) {
          const productData = dashboardData.sales.salesByProduct.map(
            (item) => ({
              Product: item.product,
              Revenue: item.revenue,
              Quantity: item.quantity,
              "Avg Price": item.quantity
                ? (item.revenue / item.quantity).toFixed(2)
                : 0,
            })
          );
          const productWs = XLSX.utils.json_to_sheet(productData);
          XLSX.utils.book_append_sheet(wb, productWs, "Product Performance");
        }

        // Order Trends
        if (dashboardData.orders) {
          const orderData = Object.entries(dashboardData.orders).map(
            ([status, count]) => ({
              Status: status.charAt(0).toUpperCase() + status.slice(1),
              Count: count,
            })
          );
          const orderWs = XLSX.utils.json_to_sheet(orderData);
          XLSX.utils.book_append_sheet(wb, orderWs, "Order Status");
        }
      }

      if (selectedDashboard === "collections") {
        // Collections Summary
        const collectionsData = dashboardData.collections || {};
        const collectionsSummary = [
          {
            Metric: "Total Outstanding",
            Value: collectionsData.totalOutstanding || 0,
          },
          {
            Metric: "Total Collected",
            Value: collectionsData.totalCollected || 0,
          },
          {
            Metric: "Collection Rate (%)",
            Value: collectionsData.collectionRate?.toFixed(2) || 0,
          },
        ];
        const collectionsWs = XLSX.utils.json_to_sheet(collectionsSummary);
        XLSX.utils.book_append_sheet(wb, collectionsWs, "Collections Summary");

        // Invoice Details
        if (collectionsData.invoices) {
          const invoiceData = collectionsData.invoices.map((inv) => ({
            "Invoice Number": inv.invoiceNumber,
            Customer: inv.customerName,
            "Total Amount": inv.totalAmount,
            "Paid Amount": inv.paidAmount,
            "Balance Amount": inv.balanceAmount,
            Status: inv.status,
            "Due Date": new Date(inv.dueDate).toLocaleDateString(),
          }));
          const invoiceWs = XLSX.utils.json_to_sheet(invoiceData);
          XLSX.utils.book_append_sheet(wb, invoiceWs, "Invoices");
        }
      }

      if (selectedDashboard === "customers") {
        // Customer Analytics
        if (dashboardData.customers?.customers) {
          const customerData = dashboardData.customers.customers.map(
            (cust) => ({
              "Customer Name": cust.businessName,
              "Total Orders": cust.totalOrders,
              "Total Spent": cust.totalSpent,
              "Average Order Value": cust.avgOrderValue?.toFixed(2) || 0,
              Status: cust.status,
            })
          );
          const customerWs = XLSX.utils.json_to_sheet(customerData);
          XLSX.utils.book_append_sheet(wb, customerWs, "Customer Analytics");
        }
      }

      // Dashboard Summary Sheet
      const dashboard = dashboardData.dashboard || {};
      const summaryData = [
        { Metric: "Total Revenue", Value: dashboard.totalRevenue || 0 },
        { Metric: "Total Orders", Value: dashboard.totalOrders || 0 },
        { Metric: "Active Customers", Value: dashboard.activeCustomers || 0 },
        { Metric: "Pending Orders", Value: dashboard.pendingOrders || 0 },
      ];
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWs, "Dashboard Summary");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${dashboardLabel}_Report_${dateRangeLabel}_${timestamp}.xlsx`;

      // Write file
      XLSX.writeFile(wb, filename);
      toast.success("Report exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    }
  };

  // Process data for KPI calculations
  const calculateKPIs = () => {
    const dashboard = dashboardData?.dashboard || {};
    const sales = dashboardData?.sales || {};
    const collections = dashboardData?.collections || {};
    const customers = dashboardData?.customers || {};

    return {
      overview: [
        {
          title: "Total Revenue",
          value: `₹${(dashboard.totalRevenue || 0).toLocaleString()}`,
          change: 12.5,
          icon: DollarSign,
          color: "emerald",
          target: `₹${((dashboard.totalRevenue || 0) * 1.1).toLocaleString()}`,
          progress: dashboard.totalRevenue
            ? Math.min(
                (dashboard.totalRevenue / (dashboard.totalRevenue * 1.1)) * 100,
                100
              )
            : 0,
        },
        {
          title: "Active Orders",
          value: `${dashboard.totalOrders || 0}`,
          change: 8.2,
          icon: ShoppingCart,
          color: "blue",
          target: `${Math.ceil((dashboard.totalOrders || 0) * 1.2)}`,
          progress: dashboard.totalOrders
            ? Math.min(
                (dashboard.totalOrders / (dashboard.totalOrders * 1.2)) * 100,
                100
              )
            : 0,
        },
        {
          title: "Active Customers",
          value: `${dashboard.activeCustomers || 0}`,
          change: 15.7,
          icon: Users,
          color: "purple",
          target: `${Math.ceil((dashboard.activeCustomers || 0) * 1.1)}`,
          progress: dashboard.activeCustomers
            ? Math.min(
                (dashboard.activeCustomers /
                  (dashboard.activeCustomers * 1.1)) *
                  100,
                100
              )
            : 0,
        },
        {
          title: "Collection Rate",
          value: `${
            collections.collectionRate
              ? collections.collectionRate.toFixed(1)
              : "0.0"
          }%`,
          change: -2.1,
          icon: Target,
          color: "orange",
          target: "95.0%",
          progress: collections.collectionRate || 0,
        },
      ],
      sales: [
        {
          title: "Total Revenue",
          value: `₹${(sales.totalRevenue || 0).toLocaleString()}`,
          change: 18.2,
          icon: TrendingUp,
          color: "emerald",
        },
        {
          title: "Total Orders",
          value: `${sales.totalOrders || 0}`,
          change: 4.1,
          icon: ShoppingCart,
          color: "blue",
        },
        {
          title: "Avg Order Value",
          value: `₹${
            sales.totalOrders
              ? Math.round((sales.totalRevenue || 0) / sales.totalOrders)
              : 0
          }`,
          change: 12.0,
          icon: DollarSign,
          color: "purple",
        },
        {
          title: "Products Sold",
          value: `${sales.salesByProduct ? sales.salesByProduct.length : 0}`,
          change: 28.5,
          icon: Zap,
          color: "orange",
        },
      ],
      collections: [
        {
          title: "Outstanding Amount",
          value: `₹${(collections.totalOutstanding || 0).toLocaleString()}`,
          change: -5.2,
          icon: AlertCircle,
          color: "red",
        },
        {
          title: "Collection Rate",
          value: `${
            collections.collectionRate
              ? collections.collectionRate.toFixed(1)
              : "0.0"
          }%`,
          change: 3.1,
          icon: CheckCircle,
          color: "emerald",
        },
        {
          title: "Total Collected",
          value: `₹${(collections.totalCollected || 0).toLocaleString()}`,
          change: -15.3,
          icon: DollarSign,
          color: "green",
        },
        {
          title: "Total Invoices",
          value: `${collections.invoices ? collections.invoices.length : 0}`,
          change: -8.7,
          icon: Activity,
          color: "blue",
        },
      ],
    };
  };

  // Process sales trend data
  const processSalesTrendData = () => {
    if (!dashboardData?.sales?.salesByDate) return [];

    return dashboardData.sales.salesByDate.map((item) => ({
      month: new Date(item.date).toLocaleDateString("en-US", {
        month: "short",
      }),
      revenue: item.revenue,
      orders: item.orders,
      customers: Math.floor(item.orders * 0.7), // Estimate based on orders
    }));
  };

  // Process product performance data
  const processProductPerformanceData = () => {
    if (!dashboardData?.sales?.salesByProduct) return [];

    return dashboardData.sales.salesByProduct.slice(0, 5).map((item) => ({
      name: item.product,
      sales: item.revenue,
      margin: Math.random() * 10 + 15, // Random margin since not in backend
      orders: item.quantity,
    }));
  };

  // Process customer segment data
  const processCustomerSegmentData = () => {
    if (!dashboardData?.customers?.customers) return [];

    const customers = dashboardData.customers.customers;
    const total = customers.length;

    // Categorize by total spent
    const enterprise = customers.filter((c) => c.totalSpent > 100000).length;
    const sme = customers.filter(
      (c) => c.totalSpent > 10000 && c.totalSpent <= 100000
    ).length;
    const startup = customers.filter((c) => c.totalSpent <= 10000).length;

    return [
      {
        name: "Enterprise",
        value: Math.round((enterprise / total) * 100),
        color: "#10b981",
      },
      { name: "SME", value: Math.round((sme / total) * 100), color: "#3b82f6" },
      {
        name: "Startup",
        value: Math.round((startup / total) * 100),
        color: "#8b5cf6",
      },
    ];
  };

  // Process payment method data (from invoices)
  const processPaymentMethodData = () => {
    if (!dashboardData?.invoices) return [];

    // Mock payment method distribution
    return [
      { name: "Bank Transfer", value: 52, amount: 234000 },
      { name: "Cheque", value: 28, amount: 126000 },
      { name: "UPI", value: 15, amount: 68000 },
      { name: "Cash", value: 5, amount: 22000 },
    ];
  };

  const processRegionWiseSalesData = () => {
    // Mock data for region-wise sales
    return [
      { region: "North", revenue: 1200000 },
      { region: "South", revenue: 950000 },
      { region: "East", revenue: 700000 },
      { region: "West", revenue: 1100000 },
      { region: "Central", revenue: 600000 },
    ];
  };

  const processSalesProjectionData = () => {
    // Mock data for last year, this year, and projection
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const lastYear = [
      80000, 90000, 95000, 100000, 110000, 120000, 130000, 125000, 115000,
      120000, 130000, 140000,
    ];
    const thisYear = [
      90000, 95000, 100000, 110000, 120000, 130000, 135000, 0, 0, 0, 0, 0,
    ]; // up to July
    // Simple projection: average growth rate
    const growth =
      thisYear.slice(0, 7).reduce((sum, v, i) => sum + (v - lastYear[i]), 0) /
      7;
    const projected = [...thisYear];
    for (let i = 7; i < 12; i++) {
      projected[i] = lastYear[i] + growth;
    }
    return months.map((month, i) => ({
      month,
      lastYear: lastYear[i],
      thisYear: thisYear[i],
      projected: projected[i],
    }));
  };

  const kpiData = calculateKPIs();
  const salesTrendData = processSalesTrendData();
  const productPerformanceData = processProductPerformanceData();
  const customerSegmentData = processCustomerSegmentData();
  const paymentMethodData = processPaymentMethodData();
  const regionWiseSalesData = processRegionWiseSalesData();
  const salesProjectionData = processSalesProjectionData();

  const dashboards = [
    { id: "overview", label: "Executive Overview", icon: BarChart3 },
    { id: "sales", label: "Sales Analytics", icon: TrendingUp },
    { id: "collections", label: "Collections Intelligence", icon: DollarSign },
    { id: "customers", label: "Customer Insights", icon: Users },
  ];

  const KPICard = ({ kpi, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-${kpi.color}-100`}>
          <kpi.icon className={`w-6 h-6 text-${kpi.color}-600`} />
        </div>
        <div
          className={`text-sm font-medium px-2 py-1 rounded-full ${
            kpi.change > 0
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {kpi.change > 0 ? "+" : ""}
          {kpi.change}%
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-2xl font-bold text-gray-900">{kpi.value}</h3>
        <p className="text-sm text-gray-600">{kpi.title}</p>

        {kpi.target && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progress to Target</span>
              <span>{kpi.progress.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(kpi.progress, 100)}%` }}
                transition={{ duration: 1, delay: index * 0.2 }}
                className={`h-2 rounded-full bg-${kpi.color}-500`}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Target: {kpi.target}</p>
          </div>
        )}
      </div>
    </motion.div>
  );

  const FilterPanel = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200"
    >
      <div className="flex items-center gap-2 mb-3">
        <Filter className="w-4 h-4 text-gray-600" />
        <h3 className="font-semibold text-gray-800">Filters</h3>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date Range
          </label>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="quarter">Last 90 days</option>
            <option value="year">Last 365 days</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Customer Type
          </label>
          <select
            value={filters.customer}
            onChange={(e) =>
              setFilters({ ...filters, customer: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Customers</option>
            <option value="enterprise">Enterprise</option>
            <option value="sme">SME</option>
            <option value="startup">Startup</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <button
          onClick={refreshDashboard}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Apply Filters
        </button>
      </div>
    </motion.div>
  );

  const renderDashboardContent = () => {
    const currentKPIs = kpiData[selectedDashboard] || kpiData.overview;
    const data = dashboardData || {};

    switch (selectedDashboard) {
      case "overview":
        return (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {currentKPIs.map((kpi, index) => (
                <KPICard key={index} kpi={kpi} index={index} />
              ))}
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Revenue Trend
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={salesTrendData}>
                    <defs>
                      <linearGradient
                        id="revenueGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#3b82f6"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#3b82f6"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#3b82f6"
                      fillOpacity={1}
                      fill="url(#revenueGradient)"
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Customer Segments
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={customerSegmentData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {customerSegmentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </motion.div>
            </div>

            {/* Performance Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Product Performance Matrix
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={productPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#6b7280" />
                  <YAxis yAxisId="left" stroke="#6b7280" />
                  <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="sales"
                    fill="#3b82f6"
                    name="Sales (₹)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="margin"
                    stroke="#10b981"
                    strokeWidth={3}
                    name="Margin %"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </motion.div>
          </div>
        );

      case "sales":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {currentKPIs.map((kpi, index) => (
                <KPICard key={index} kpi={kpi} index={index} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Sales vs Orders Correlation
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={salesTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      name="Revenue"
                    />
                    <Line
                      type="monotone"
                      dataKey="orders"
                      stroke="#10b981"
                      strokeWidth={3}
                      name="Orders"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Top Products by Revenue
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productPerformanceData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" stroke="#6b7280" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      stroke="#6b7280"
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Bar dataKey="sales" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>
            </div>
            {/* Region-wise Sales BarChart */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Region-wise Sales
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={regionWiseSalesData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis
                    dataKey="region"
                    type="category"
                    stroke="#6b7280"
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value) => `₹${value.toLocaleString()}`}
                  />
                  <Bar dataKey="revenue" fill="#3b82f6" name="Revenue (₹)" />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
            {/* Sales Projection LineChart */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Sales Projection (vs Previous Year)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesProjectionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value) => `₹${value.toLocaleString()}`}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="lastYear"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    name="Last Year"
                  />
                  <Line
                    type="monotone"
                    dataKey="thisYear"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    name="This Year"
                  />
                  <Line
                    type="dashed"
                    dataKey="projected"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Projected"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
          </div>
        );

      case "collections":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {currentKPIs.map((kpi, index) => (
                <KPICard key={index} kpi={kpi} index={index} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Payment Methods Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={paymentMethodData}
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                    >
                      {paymentMethodData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b"][index]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Collections Timeline
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={salesTrendData}>
                    <defs>
                      <linearGradient
                        id="collectionsGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#10b981"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#10b981"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#10b981"
                      fillOpacity={1}
                      fill="url(#collectionsGradient)"
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>
            </div>

            {/* Collections Data Table */}
            {dashboardData?.collections && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Invoice Collections Report
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Paid
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Outstanding
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dashboardData.collections.invoices?.map(
                        (invoice, index) => (
                          <motion.tr
                            key={invoice.invoiceId}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              #{invoice.invoiceId}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {invoice.customerName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{invoice.amount.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                              ₹{invoice.totalPaid.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                              ₹{invoice.outstanding.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  invoice.status === "paid"
                                    ? "bg-green-100 text-green-800"
                                    : invoice.status === "partially_paid"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {invoice.status === "paid"
                                  ? "Paid"
                                  : invoice.status === "partially_paid"
                                  ? "Partial"
                                  : "Unpaid"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(invoice.dueDate).toLocaleDateString()}
                            </td>
                          </motion.tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </div>
        );

      case "customers":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {kpiData.overview.map((kpi, index) => (
                <KPICard key={index} kpi={kpi} index={index} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Customer Acquisition Trend
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={salesTrendData}>
                    <defs>
                      <linearGradient
                        id="customerGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#8b5cf6"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#8b5cf6"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="customers"
                      stroke="#8b5cf6"
                      fillOpacity={1}
                      fill="url(#customerGradient)"
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Customer Value Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart data={productPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="orders" stroke="#6b7280" name="Orders" />
                    <YAxis dataKey="sales" stroke="#6b7280" name="Revenue" />
                    <Tooltip
                      cursor={{ strokeDasharray: "3 3" }}
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Scatter dataKey="sales" fill="#8b5cf6" />
                  </ScatterChart>
                </ResponsiveContainer>
              </motion.div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Business Intelligence Dashboard
            </h1>
            <p className="mt-2 text-gray-600">
              Advanced analytics and insights for data-driven decisions
            </p>
          </div>

          <div className="flex items-center gap-3 mt-4 lg:mt-0">
            {/* Real-time Status */}
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Live Updates</span>
              </div>
              <div className="text-xs text-gray-500">
                Last updated:{" "}
                {dashboardLastRefresh?.toLocaleTimeString() || "Never"}
              </div>
            </div>

            <button
              onClick={refreshDashboard}
              disabled={dashboardLoading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw
                className={`w-4 h-4 ${dashboardLoading ? "animate-spin" : ""}`}
              />
              {dashboardLoading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              onClick={handleExportReport}
              disabled={dashboardLoading || !dashboardData}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Eye className="w-4 h-4" />
              Live View
            </button>
          </div>
        </div>
      </motion.div>

      {/* Dashboard Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-2 shadow-sm border border-gray-200 mb-6"
      >
        <div className="flex flex-wrap gap-2">
          {dashboards.map((dashboard) => {
            const Icon = dashboard.icon;
            return (
              <button
                key={dashboard.id}
                onClick={() => setSelectedDashboard(dashboard.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  selectedDashboard === dashboard.id
                    ? "bg-blue-600 text-white shadow-lg"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                {dashboard.label}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1">
          <FilterPanel />
        </div>

        {/* Dashboard Content */}
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {dashboardError && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-red-50 border border-red-200 rounded-md p-4 mb-6"
              >
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      {dashboardError}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {dashboardLoading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center h-96"
              >
                <LoadingSpinner />
              </motion.div>
            ) : (
              <motion.div
                key={selectedDashboard}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderDashboardContent()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
